const toCartQuantity = (value, fallback = 0) => {
  const quantity = Number(value);

  if (!Number.isFinite(quantity)) {
    return fallback;
  }

  return Math.max(0, Math.floor(quantity));
};

export const calculateDiscountedUnitPrice = (item) => {
  const price = Number(item?.price || 0);
  const discountPercent = item?.discount_percent || item?.discountPercent || 0;

  if (discountPercent > 0) {
    return Math.round(price * (1 - discountPercent / 100));
  }

  return price;
};

export const calculateCartItemPrice = (item) =>
  calculateDiscountedUnitPrice(item) * toCartQuantity(item?.quantity, 0);

export const calculateCartTotal = (items = []) =>
  items.reduce((total, item) => total + calculateCartItemPrice(item), 0);

export const calculateCartTotalItems = (items = []) =>
  items.reduce((total, item) => total + toCartQuantity(item?.quantity, 0), 0);

export const isItemInCart = (items = [], itemId) =>
  items.some((item) => item.id === itemId);

export const getCartItemQuantity = (items = [], itemId) => {
  const item = items.find((entry) => entry.id === itemId);
  return item ? toCartQuantity(item.quantity, 0) : 0;
};

export const addCartItem = (items = [], item, quantity = 1) => {
  const nextQuantity = toCartQuantity(quantity, 0);

  if (item?.id == null || nextQuantity <= 0) {
    return items;
  }

  const existingItem = items.find((cartItem) => cartItem.id === item.id);

  if (existingItem) {
    return items.map((cartItem) =>
      cartItem.id === item.id
        ? {
            ...cartItem,
            quantity: toCartQuantity(cartItem.quantity, 0) + nextQuantity,
          }
        : cartItem,
    );
  }

  return [...items, { ...item, quantity: nextQuantity }];
};

export const removeCartItem = (items = [], itemId) =>
  items.filter((item) => item.id !== itemId);

export const removeCartItemQuantity = (items = [], itemId, quantity = null) => {
  const item = items.find((cartItem) => cartItem.id === itemId);

  if (!item) {
    return items;
  }

  const nextQuantity = quantity === null ? null : toCartQuantity(quantity, 0);
  const currentQuantity = toCartQuantity(item.quantity, 0);

  if (nextQuantity === 0) {
    return items;
  }

  if (nextQuantity === null || nextQuantity >= currentQuantity) {
    return removeCartItem(items, itemId);
  }

  return items.map((cartItem) =>
    cartItem.id === itemId
      ? { ...cartItem, quantity: currentQuantity - nextQuantity }
      : cartItem,
  );
};

export const updateCartItemQuantity = (items = [], itemId, quantity) => {
  const nextQuantity = toCartQuantity(quantity, 0);

  if (nextQuantity <= 0) {
    return removeCartItem(items, itemId);
  }

  return items.map((item) =>
    item.id === itemId ? { ...item, quantity: nextQuantity } : item,
  );
};

export const clearCartItems = () => [];

export const CART_ACTIONS = {
  hydrate: "hydrate",
  addItem: "add-item",
  removeItem: "remove-item",
  removeItemQuantity: "remove-item-quantity",
  updateQuantity: "update-quantity",
  clear: "clear",
};

export const reduceCartItems = (items = [], action = {}) => {
  switch (action.type) {
    case CART_ACTIONS.hydrate:
      return Array.isArray(action.items) ? action.items : [];
    case CART_ACTIONS.addItem:
      return addCartItem(items, action.item, action.quantity);
    case CART_ACTIONS.removeItem:
      return removeCartItem(items, action.itemId);
    case CART_ACTIONS.removeItemQuantity:
      return removeCartItemQuantity(items, action.itemId, action.quantity);
    case CART_ACTIONS.updateQuantity:
      return updateCartItemQuantity(items, action.itemId, action.quantity);
    case CART_ACTIONS.clear:
      return clearCartItems();
    default:
      return items;
  }
};
